import randomize from "randomatic";

import store, { db } from "../components/auth/firebase";

const logQueue = [];

const logToDatabase = (videoId, action) => {
  const { uid = "unknown" } = store.getState().user;

  const date = new Date();

  if (logQueue.length > 0 && logQueue[logQueue.length - 1] === action) {
    return null;
  }
  logQueue.push(action);

  return db
    .collection("projectsLogging")
    .doc(videoId)
    .set(
      {
        [date.toISOString()]: {
          uid,
          action,
        },
      },
      { merge: true }
    );
};

/**
 * Add Project to 'projects' state.
 * @param {object} snapshot - Document snapshot from Firebase of the given project.
 */
export const addProject = (id, snapshot) => {
  return store.dispatch({
    type: "ADD_PROJECT",
    project: { [id]: { ...snapshot, id } },
  });
};

/**
 * Add Project to 'projects_user_specific' state.
 * @param {object} videoId - videoId of the projects.
 */
export const addProjectUserSpecific = (videoId) => {
  logToDatabase(videoId, `Opened Project (${videoId})`);

  return store.dispatch({
    type: "ADD_PROJECT_USER",
    project: {
      [videoId]: { selectedLang: "", selectedTrack: "", selectedCue: "" },
    },
  });
};

/**
 * Save Project to firestore.
 * @param {string} videoId - Video ID of the project.
 * @param {boolean} notify - Should the user get a notification that the project was saved. Default 'False'.
 */
export const saveProject = (videoId, notify = false) => {
  logToDatabase(videoId, `Saved Project (${videoId})`);
  return store.dispatch({
    type: "SAVE_PROJECT",
    videoId,
    notify,
  });
};

/**
 * Add recent project users firestore.
 * @param {string} videoId - Video ID of the project.
 * @param {string} uid - UID of the user.
 * @param {array} recent_projects - Current array of recent projects.
 */
export const addRecentProject = (videoId, uid, recent_projects) => {
  return store.dispatch({
    type: "ADD_RECENT_PROJECT",
    videoId,
    uid,
    recent_projects,
  });
};

/**
 * Add Language to a project
 * @param {string} videoId - Video ID of the project
 * @param {string} lang - Language to add
 * @param {string} label - Label of language
 * This will also set the activeLanguage to this new language
 */
export const addLang = (videoId, lang, label) => {
  const dispatch = store.dispatch({
    type: "ADD_LANG",
    videoId,
    lang,
    label,
  });

  selectLang(videoId, lang);
  selectTrack(videoId, "");
  addTrack(videoId, lang, "subtitles");
  selectCue(videoId, "");

  saveProject(videoId, false);

  logToDatabase(videoId, `Added Language (${lang})`);

  return dispatch;
};

/**
 * Remove Language from a project
 * @param {string} videoId - Video ID of the project
 * @param {string} lang - Language to remove
 * This will also set the activeLanguage and activeTrack to ''
 */
export const removeLang = (videoId, lang) => {
  const dispatch = store.dispatch({
    type: "REMOVE_LANG",
    videoId,
    lang,
  });

  selectLang(videoId, "");
  selectTrack(videoId, "");
  selectCue(videoId, "");

  saveProject(videoId, false);

  logToDatabase(videoId, `Removed Language (${lang})`);

  return dispatch;
};

/**
 * Select Language within a project
 * @param {string} videoId - Video ID of the project
 * @param {string} lang - Language to select
 */
export const selectLang = (videoId, lang) => {
  store.dispatch({
    type: "SELECT_LANG",
    videoId,
    lang,
  });

  logToDatabase(videoId, `Selected Language (${lang})`);

  selectTrack(videoId, "subtitles");
  selectCue(videoId, "");
};

/**
 * Add Track to a language within a project
 * @param {string} videoId - Video ID of the project
 * @param {string} lang - Language to add
 * @param {string} track - Track to add
 * This will also set the active track to this new track
 */
export const addTrack = (videoId, lang, track) => {
  store.dispatch({
    type: "ADD_TRACK",
    videoId,
    lang,
    track,
  });

  logToDatabase(videoId, `Added Track (${track} - ${lang})`);

  selectTrack(videoId, track);
};

/**
 * Copy Track within a project
 * @param {string} videoId - Video ID of the project
 * @param {string} lang - Language to copy track FROM
 * @param {string} langTo - Language to copy track TO
 * @param {string} track - Track to copy
 * @param {string} trackTo - Track to add to
 */
export const copyTrack = (videoId, lang, langTo, track, trackTo) => {
  store.dispatch({
    type: "COPY_TRACK",
    videoId,
    lang,
    langTo,
    track,
    trackTo,
  });

  selectTrack(videoId, track);
};

/**
 * Select a track within a language
 * @param  {string} videoId - Video ID of the project
 * @param  {string} track - Track to select
 */
export const selectTrack = (videoId, track) => {
  store.dispatch({
    type: "SELECT_TRACK",
    videoId,
    track,
  });

  //  saveProject(videoId, false);
};

/**
 * Remove a track from within a language
 * @param  {string} videoId - Video ID of the project
 * @param  {string} lang - Language to remove the track from
 * @param  {string} track - Track to remove i.e. 'subtitles'
 * This will also set selectedTrack and selectedCue to ''
 */
export const removeTrack = (videoId, lang, track) => {
  store.dispatch({
    type: "REMOVE_TRACK",
    videoId,
    lang,
    track,
  });

  selectTrack(videoId, "");
  selectCue(videoId, "");

  logToDatabase(videoId, `Removed Track (${track} - ${lang})`);

  saveProject(videoId, false);
};

/**
 * Add Cue to a track
 * @param {string} videoId - Video ID of the project
 * @param {Object[]} cue - Object to add as the cue
 * @param {integer} cue[].start - Start time of the cue
 * @param {integer} cue[].end - End time of the cue
 * @param {string} cue[].text - Text of the string
 * @param {Object[]} selected - Object containing the user_speific selections of lang, track & cue
 * @param {Boolean} save - Should the project be saved
 * This will also set the activeCue to this new cue
 * ID will be auto-generated by this function: randomize("A0", 5)
 */
export const addCue = (videoId, cue, selected, save = true) => {
  let randId = randomize("A0", 5);
  let identifier = "";
  if (cue.identifier) {
    identifier = cue.identifier;
  }
  let styles = "";
  if (cue.styles) {
    styles = cue.styles;
  }

  store.dispatch({
    type: "ADD_CUE",
    videoId,
    cue: {
      ...cue,
      start: parseFloat(cue.start.toFixed(2)),
      end: parseFloat(cue.end.toFixed(2)),
      identifier,
      styles,
      id: randId,
    },
    selected,
  });

  selectCue(videoId, randId, selected);

  if (save === true) saveProject(videoId, false);
};

/**
 * Remove cue from selected track
 * @param  {string} videoId - Video ID of the project
 * @param  {string} id - ID of the cue to remove
 * * @param  {Object[]} selected - Currently selected by user
 * This will also set the selectedCue to ''
 */
export const removeCue = (videoId, selected) => {
  store.dispatch({
    type: "REMOVE_CUE",
    videoId,
    selected,
  });

  selectCue(videoId, "", selected);
  saveProject(videoId, false);
};

/**
 * Update cue within a track
 * @param  {string} videoId - Video ID of the project
 * @param  {string} id - ID of the cue to update
 * @param  {Object[]} cue - Object to add as the cue
 * @param  {integer} cue[].start - Start time of the cue
 * @param  {integer} cue[].end - End time of the cue
 * @param  {string} cue[].text - Text of the string
 * @param  {Object[]} selected - Currently selected by user
 */
export const updateCue = (videoId, id, cue, selected) => {
  store.dispatch({
    type: "UPDATE_CUE",
    videoId,
    id,
    cue,
    selected,
  });

  if (cue.start && cue.end) {
    saveProject(videoId, false);
  }
};

/**
 * Select a cue
 * @param  {string} videoId - Video ID of the project
 * @param  {string} id - ID of the cue to select
 * @param {Object[]} selected - Object containing the user_speific selections of lang, track & cue
 */
export const selectCue = (videoId, id, selected) => {
  store.dispatch(
    {
      type: "SELECT_CUE",
      videoId,
      id,
    },
    selected
  );
};

/**
 * Add ASR object for processing
 * @param  {string} videoId - Video ID of the project
 */
export const addAsr = (videoId) => {
  store.dispatch({
    type: "ADD_ASR",
    videoId,
  });

  logToDatabase(videoId, `Triggered ASR (${videoId})`);
};
